import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { colourForDriverId } from '../theme/tokens';
import type { AnnualStrategicDriver } from '../types/models';

// ---------------------------------------------------------------------------
// DriverPill — the one way to render a strategic driver link next to a work
// item or project. Keeps colour assignment deterministic across the app so
// the same driver reads the same everywhere. When a driver is absent, we
// render a dashed "Unaligned" pill in muted neutral.
//
// Note: Radix Tooltip needs a <TooltipProvider> somewhere up the tree to
// work. The dashboard already provides one. If a consumer renders this in
// isolation, they must wrap with TooltipProvider themselves.
// ---------------------------------------------------------------------------

interface DriverPillProps {
  driver?: AnnualStrategicDriver | null;
  size?: 'compact' | 'standard';
  onClick?: () => void;
}

export function DriverPill({ driver, size = 'standard', onClick }: DriverPillProps) {
  const isCompact = size === 'compact';
  const baseClasses = cn(
    'relative inline-flex items-center overflow-hidden whitespace-nowrap rounded-full border max-w-[240px]',
    isCompact ? 'h-5 px-2.5 text-[11px]' : 'h-6 px-3 text-xs',
    'font-medium',
    onClick && 'cursor-pointer transition-colors hover:bg-accent',
  );

  // Render as a real <button> only when there is an onClick — otherwise
  // assistive tech announces a disabled button, and inside a list row the
  // button would also nest inside another interactive. The non-interactive
  // path renders a <span> via Slot-less trigger.
  if (!driver) {
    const unalignedClasses = cn(
      baseClasses,
      'border-dashed border-border bg-transparent text-muted-foreground',
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {onClick ? (
            <button type="button" onClick={onClick} className={unalignedClasses}>
              Unaligned
            </button>
          ) : (
            <span className={unalignedClasses}>Unaligned</span>
          )}
        </TooltipTrigger>
        <TooltipContent>Not linked to a strategic driver</TooltipContent>
      </Tooltip>
    );
  }

  const colour = colourForDriverId(driver.id);
  const tip =
    driver.progressPercentage !== undefined
      ? `${driver.title} — ${driver.progressPercentage}% of target`
      : driver.title;
  const pillClasses = cn(
    baseClasses,
    'border-border bg-transparent text-foreground pl-3.5',
  );
  const swatch = (
    <span
      aria-hidden
      className="absolute left-0 top-1 bottom-1 w-[3px] rounded"
      style={{ backgroundColor: colour }}
    />
  );
  const label = <span className="truncate">{driver.title}</span>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {onClick ? (
          <button type="button" onClick={onClick} className={pillClasses}>
            {swatch}
            {label}
          </button>
        ) : (
          <span className={pillClasses}>
            {swatch}
            {label}
          </span>
        )}
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

export default DriverPill;
